import * as React from 'react';
import { useState } from 'react';
import { capitalize } from 'lodash';
import { getCityAllCourtTagVenueUrlWithSport } from 'constants/pages';
import { MILES_NEARYBY_COURTS } from 'constants/seo';
import { isSeoCityMarket } from 'utils/shared/seo/isSeoCityMarket';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Breadcrumbs from 'components/Breadcrumbs';
import DiscoverPage from 'components/DiscoverPage/DiscoverPage';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import Footer from 'components/Footer';
import Link from 'components/Link';
import CardVenue from 'components/cards/CardVenue';
import CityLocationProvider from 'components/utilities/CityLocationProvider';
import Head from 'components/utilities/Head';
import CityExtraContent from './CityExtraContent';
import { Props, TagLinkKey, tagLinks } from './types';
import useCityVenues from './useCityVenues';
import {
  generateCourtsBreadcrumbs,
  generateCourtsTagBreadcrumbs,
  getCourtsSeoDescription,
  getTitleLocationName,
} from './utils';

const TagLink = ({
  idOrSlug,
  tagSlug,
  sport,
}: {
  idOrSlug: string;
  tagSlug: TagLinkKey;
  sport?: string;
}) => {
  const { name, Svg, slug } = tagLinks[tagSlug];
  return (
    <Link
      href={getCityAllCourtTagVenueUrlWithSport(idOrSlug, slug, sport)}
      className="flex w-1/3 flex-col items-center justify-between rounded-lg bg-color-bg-lightmode-tertiary px-ds-lg py-ds-md dark:bg-color-bg-darkmode-tertiary md:w-1/5 md:flex-row"
    >
      <div className="typography-product-button-label-small text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {name}
      </div>
      <div className="flex justify-end">
        <Svg className="h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
      </div>
    </Link>
  );
};

export default function CityVenuesPage({
  idOrSlug,
  city,
  metaTitle,
  metaDescription,
  availableTags,
  tagName,
  tagSlug,
  jsonLd,
  nearbyVenues,
  sport,
  tournaments,
  stateTournaments,
  coaches,
  topCoaches,
  topClinics,
  coachStarterProfiles,
  groups,
  noIndex,
}: Props) {
  if (!city) {
    return null;
  }

  const shouldNoIndex = noIndex ?? !isSeoCityMarket(city);
  const [isOpenMap, setIsOpenMap] = useState(false);

  const { VenueList, courtMapProps, onAddressChange } = useCityVenues({
    city,
    nearbyVenues,
  });

  return (
    <>
      <Head
        canonicalType="currentUrlStripQuery"
        title={metaTitle}
        description={metaDescription}
        noIndex={shouldNoIndex}
      />
      <CityLocationProvider city={city}>
        <SafeAreaPage
          isShowTopNav
          isHideSidebar
          shouldHideTabBar={isOpenMap}
          topNavSearch={{
            isEnabled: true,
            isSecondRowOverlay: isOpenMap,
            isSecondRowScrollable: !isOpenMap,
            defaultExploreType: ExploreAllMarketPlacesTypesEnum.Courts,
            onAddressChange,
            instant: true,
          }}
        >
          <main className="flex grow flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
            {/* Address bar section */}
            <DiscoverPage
              List={VenueList}
              mapProps={courtMapProps}
              showTabs={false}
              showAddressBar={false}
              onMapToggle={setIsOpenMap}
              extraHeaderContent={
                <>
                  {/* Breadcrumbs and title section */}
                  <div className="border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode">
                    <div className="mx-auto max-w-7xl pb-ds-lg md:flex-row md:items-center">
                      <div className="mb-2">
                        <Breadcrumbs
                          breadcrumbs={
                            tagName && tagSlug
                              ? generateCourtsTagBreadcrumbs({
                                  cityName: city?.name || '',
                                  citySlug: city?.slug || '',
                                  regionName: city?.countrySubdivision?.name || '',
                                  regionSlug: city?.countrySubdivision?.slug || '',
                                  countryId: city?.countrySubdivision?.country?.id || '',
                                  countryName: city?.countrySubdivision?.country?.name || '',
                                  countrySlug: city?.countrySubdivision?.country?.slug || '',
                                  tagName,
                                  tagSlug,
                                  sport: sport || '',
                                }).ui
                              : generateCourtsBreadcrumbs({
                                  cityName: city?.name || '',
                                  citySlug: city?.slug || '',
                                  regionName: city?.countrySubdivision?.name || '',
                                  regionSlug: city?.countrySubdivision?.slug || '',
                                  countryId: city?.countrySubdivision?.country?.id || '',
                                  countryName: city?.countrySubdivision?.country?.name || '',
                                  countrySlug: city?.countrySubdivision?.country?.slug || '',
                                  sport: sport || '',
                                }).ui
                          }
                        />
                      </div>
                      <h1 className="typography-product-heading">
                        {`${tagName ? `${tagName} ` : ''}${capitalize(
                          sport,
                        )} Courts in ${getTitleLocationName(city)}`}
                        <span className="hidden lg:inline">
                          {` (${city?.venues?.length} locations)`}
                        </span>
                      </h1>
                      <p className="typography-product-caption mt-ds-sm max-w-3xl text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                        {getCourtsSeoDescription({ city, sport, tagSlug })}
                      </p>
                      {availableTags && (
                        <div className="mt-ds-lg flex gap-ds-sm md:gap-ds-xl">
                          {availableTags.map((slug) => {
                            return (
                              <TagLink
                                key={idOrSlug}
                                idOrSlug={idOrSlug}
                                tagSlug={slug}
                                sport={sport}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Category tags section */}
                  {/* {availableTags && !!availableTags.length && (
                    <div className="mx-auto w-full">
                      <div className="w-full border-b border-color-border-input-lightmode pb-8 dark:border-color-border-input-darkmode">
                        <h2 className="typography-product-subheading">Explore by category</h2>
                        <div className="mt-ds-lg flex gap-ds-sm md:gap-ds-xl">
                          {availableTags.map((slug) => {
                            return <TagLink key={idOrSlug} idOrSlug={idOrSlug} tagSlug={slug} sport={sport} />;
                          })}
                        </div>
                      </div>
                    </div>
                  )} */}
                </>
              }
            />
          </main>
          {!tagName && (
            <CityExtraContent
              city={city}
              sport={sport}
              tournaments={tournaments}
              stateTournaments={stateTournaments}
              coaches={coaches}
              topCoaches={topCoaches}
              topClinics={topClinics}
              coachStarterProfiles={coachStarterProfiles}
              groups={groups}
            />
          )}
          <Footer />
        </SafeAreaPage>
      </CityLocationProvider>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
